import { MenuItem, Select } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import defaultImage from "../../../assets/images/default.jpg";
import { generateGifFromVideo } from "../../../utils/gifGenerator";
import { permissionError } from "../../../util/Alert";
import Button from "../../extra/Button";
import Input from "../../extra/Input";
import { CLOSE_DIALOGUE } from "../../store/dialogue/dialogue.type";
import { getFakeSellerDropDown } from "../../store/fake Seller/fakeSeller.action";
import {
  createFakeReel,
  updateFakeReel,
} from "../../store/fakeReels/fakeReels.action";
import { getSellerProduct } from "../../store/seller/seller.action";

const VIDEO_TYPE = {
  FILE: 1,
  LINK: 2,
};

const INITIAL_ERROR_STATE = {
  video: "",
  videoPath: "",
  sellerType: "",
  selectedOptions: "",
  thumbnail: "",
  thumbnailPath: "",
  previewGif: "",
};

const FakeReelDialogue = (props) => {
  const dispatch = useDispatch();

  const { fakeSeller } = useSelector((state) => state.fakeSeller);
  const hasPermission = useSelector((state) => state.admin.admin.flag);
  const { dialogueData } = useSelector((state) => state.dialogue);
  const { product } = useSelector((state) => state.seller);

  const [mongoId, setMongoId] = useState("");
  const [sellerType, setSellerType] = useState("");
  const [videoType, setVideoType] = useState(VIDEO_TYPE.FILE);
  const [videoPath, setVideoPath] = useState(null);

  const [thumbnail, setThumbnail] = useState([]);
  const [thumbnailPath, setThumbnailPath] = useState("");
  const [duration, setDuration] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [video, setVideo] = useState({ file: "" });

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [previewGif, setPreviewGif] = useState(null);
  const [previewGifUrl, setPreviewGifUrl] = useState(null);
  const [isGeneratingGif, setIsGeneratingGif] = useState(false);
  const [previewGifError, setPreviewGifError] = useState("");

  const previewGifUrlRef = useRef(null);
  const thumbnailPathRef = useRef(null);
  const gifGenerationIdRef = useRef(0);
  const linkDebounceRef = useRef(null);
  const gifGenerationRequestedRef = useRef(false);

  const [error, setError] = useState(INITIAL_ERROR_STATE);

  const revokePreviewGifUrl = useCallback(() => {
    if (previewGifUrlRef.current) {
      URL.revokeObjectURL(previewGifUrlRef.current);
      previewGifUrlRef.current = null;
    }
  }, []);

  const revokeThumbnailPath = useCallback(() => {
    if (thumbnailPathRef.current) {
      URL.revokeObjectURL(thumbnailPathRef.current);
      thumbnailPathRef.current = null;
    }
  }, []);

  // INIT from dialogueData
  useEffect(() => {
    if (dialogueData) {
      setMongoId(dialogueData?._id || "");
      setSellerType(dialogueData?.sellerId?._id || "");
      setVideoType(dialogueData?.videoType || VIDEO_TYPE.FILE);
      setVideoPath(dialogueData?.video || null);

      setVideo({ file: "" });
      setSelectedVideo(null);
      gifGenerationRequestedRef.current = false;
      revokePreviewGifUrl();
      revokeThumbnailPath();
      setPreviewGif(null);
      setPreviewGifUrl(null);
      setIsGeneratingGif(false);
      setPreviewGifError("");

      setThumbnail(dialogueData?.thumbnail || "");
      setThumbnailPath(dialogueData?.thumbnail || "");

      if (dialogueData?.duration) {
        setDuration(dialogueData.duration);
      }

      if (dialogueData?.productId && Array.isArray(dialogueData.productId)) {
        setSelectedOptions(dialogueData.productId.map((item) => item._id));
      } else {
        setSelectedOptions([]);
      }
    }
  }, [dialogueData, revokePreviewGifUrl, revokeThumbnailPath]);

  useEffect(() => {
    return () => {
      revokePreviewGifUrl();
      revokeThumbnailPath();
      if (linkDebounceRef.current) {
        clearTimeout(linkDebounceRef.current);
      }
    };
  }, [revokePreviewGifUrl, revokeThumbnailPath]);

  // Fetch sellers + products
  useEffect(() => {
    dispatch(getFakeSellerDropDown());
  }, [dispatch]);

  useEffect(() => {
    if (sellerType) {
      dispatch(getSellerProduct(sellerType));
    }
  }, [dispatch, sellerType]);

  // helper: get duration from file
  const getVideoDuration = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const videoEl = document.createElement("video");
      videoEl.preload = "metadata";

      videoEl.onloadedmetadata = () => {
        window.URL.revokeObjectURL(videoEl.src);
        resolve(videoEl.duration);
      };

      videoEl.onerror = () => {
        reject("Error loading video metadata");
      };

      videoEl.src = URL.createObjectURL(file);
    });
  }, []);

  const getVideoDurationFromUrl = useCallback((url) => {
    return new Promise((resolve, reject) => {
      const videoEl = document.createElement("video");
      videoEl.preload = "metadata";

      videoEl.onloadedmetadata = () => {
        resolve(videoEl.duration);
      };

      videoEl.onerror = () => {
        reject("Error loading video metadata");
      };

      videoEl.src = url;
    });
  }, []);

  const generateThumbnailFromSource = useCallback((source) => {
    return new Promise((resolve, reject) => {
      const videoEl = document.createElement("video");
      videoEl.preload = "metadata";
      videoEl.crossOrigin = "anonymous";

      const objectUrl =
        source instanceof File || source instanceof Blob
          ? URL.createObjectURL(source)
          : null;

      videoEl.onloadedmetadata = () => {
        videoEl.currentTime = 1;
      };

      videoEl.onseeked = () => {
        const canvas = document.createElement("canvas");
        canvas.width = videoEl.videoWidth;
        canvas.height = videoEl.videoHeight;
        canvas.getContext("2d").drawImage(videoEl, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
          }
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to create thumbnail blob"));
          }
        }, "image/jpeg");
      };

      videoEl.onerror = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
        reject(new Error("Error loading video for thumbnail"));
      };

      videoEl.src = objectUrl || source;
    });
  }, []);

  const resetGifPreview = useCallback(() => {
    revokePreviewGifUrl();
    setPreviewGif(null);
    setPreviewGifUrl(null);
    setPreviewGifError("");
    setError((prev) => ({ ...prev, previewGif: "" }));
  }, [revokePreviewGifUrl]);

  const applyGifResult = useCallback((gifBlob, sourceName) => {
    const gifFileName = `${(sourceName || "video").replace(/\.[^/.]+$/, "")}.gif`;
    const gifFile = new File([gifBlob], gifFileName, { type: "image/gif" });
    const gifUrl = URL.createObjectURL(gifFile);

    previewGifUrlRef.current = gifUrl;
    setPreviewGif(gifFile);
    setPreviewGifUrl(gifUrl);
  }, []);

  const applyThumbnailResult = useCallback((thumbnailBlob, sourceName) => {
    const thumbnailFileName = `${(sourceName || "video").replace(/\.[^/.]+$/, "")}.jpeg`;
    const thumbFile = new File([thumbnailBlob], thumbnailFileName, {
      type: "image/jpeg",
    });
    const thumbUrl = URL.createObjectURL(thumbFile);

    thumbnailPathRef.current = thumbUrl;
    setThumbnail(thumbFile);
    setThumbnailPath(thumbUrl);
  }, []);

  const startThumbnailGeneration = useCallback(
    async (videoSource, sourceName) => {
      try {
        const thumbnailBlob = await generateThumbnailFromSource(videoSource);
        applyThumbnailResult(thumbnailBlob, sourceName);
      } catch (err) {
        console.error("Thumbnail generation failed:", err);
      }
    },
    [applyThumbnailResult, generateThumbnailFromSource]
  );

  const startGifGeneration = useCallback(
    async (videoSource, sourceName) => {
      const generationId = ++gifGenerationIdRef.current;
      setIsGeneratingGif(true);

      try {
        const gifBlob = await generateGifFromVideo(videoSource);

        if (generationId !== gifGenerationIdRef.current) {
          return;
        }

        applyGifResult(gifBlob, sourceName);
      } catch (err) {
        if (generationId !== gifGenerationIdRef.current) {
          return;
        }
        console.error("GIF generation failed:", err);
        const message =
          "Failed to generate GIF preview. Please try another video or a different browser.";
        setPreviewGifError(message);
        setError((prev) => ({ ...prev, previewGif: message }));
      } finally {
        if (generationId === gifGenerationIdRef.current) {
          setIsGeneratingGif(false);
        }
      }
    },
    [applyGifResult]
  );

  const fetchVideoFromUrl = useCallback(async (url) => {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch video: ${response.status}`);
    }

    const blob = await response.blob();
    const urlPath = url.split("?")[0];
    const extension = urlPath.split(".").pop() || "mp4";

    return new File([blob], `video.${extension}`, {
      type: blob.type || "video/mp4",
    });
  }, []);

  const generateGifFromLink = useCallback(
    async (url) => {
      gifGenerationRequestedRef.current = true;
      resetGifPreview();
      revokeThumbnailPath();
      setThumbnail("");
      setThumbnailPath("");

      try {
        const videoDuration = await getVideoDurationFromUrl(url);
        setDuration(videoDuration);
      } catch (err) {
        console.error(err);
      }

      try {
        const videoFile = await fetchVideoFromUrl(url);
        await Promise.all([
          startThumbnailGeneration(videoFile, videoFile.name),
          startGifGeneration(videoFile, videoFile.name),
        ]);
      } catch (err) {
        console.error("Link video fetch/GIF generation failed:", err);
        const message =
          "Failed to generate GIF preview from link. Please check the URL and try again.";
        setPreviewGifError(message);
        setError((prev) => ({ ...prev, previewGif: message }));
      }
    },
    [
      fetchVideoFromUrl,
      getVideoDurationFromUrl,
      resetGifPreview,
      revokeThumbnailPath,
      startGifGeneration,
      startThumbnailGeneration,
    ]
  );

  const handleVideo = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      setError((prev) => ({ ...prev, video: "Please select a video!" }));
      return;
    }

    gifGenerationRequestedRef.current = true;
    resetGifPreview();
    revokeThumbnailPath();
    setThumbnail("");
    setThumbnailPath("");

    setSelectedVideo(file);
    setVideoPath(URL.createObjectURL(file));
    setVideo({ file });
    setError((prev) => ({ ...prev, video: "" }));

    try {
      const videoDuration = await getVideoDuration(file);
      setDuration(videoDuration);
    } catch (err) {
      console.error(err);
    }

    await Promise.all([
      startThumbnailGeneration(file, file.name),
      startGifGeneration(file, file.name),
    ]);
  };

  const handleVideoLinkChange = (value) => {
    const encodedPath = value ? encodeURI(value) : "";
    setVideoPath(encodedPath || null);

    if (linkDebounceRef.current) {
      clearTimeout(linkDebounceRef.current);
    }

    if (!value) {
      gifGenerationRequestedRef.current = false;
      resetGifPreview();
      revokeThumbnailPath();
      setThumbnail("");
      setThumbnailPath("");
      setDuration(0);
      setError((prev) => ({
        ...prev,
        video: "Video Link Is Required",
      }));
      return;
    }

    setError((prev) => ({ ...prev, video: "" }));

    linkDebounceRef.current = setTimeout(() => {
      generateGifFromLink(encodedPath);
    }, 600);
  };

  const renderGifPreviewBox = (fallbackImageSrc) => (
    <div
      style={{
        width: "200px",
        height: "200px",
        position: "relative",
        flexShrink: 0,
        backgroundColor: "#f5f5f5",
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        overflow: "hidden",
      }}
    >
      {isGeneratingGif && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.9)",
            zIndex: 1,
          }}
        >
          <div className="loader"></div>
          <span
            style={{
              fontSize: "12px",
              marginTop: "8px",
              color: "#333",
            }}
          >
            Generating GIF...
          </span>
        </div>
      )}
      {previewGifUrl && !isGeneratingGif && (
        <img
          src={previewGifUrl}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
          alt="GIF preview"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultImage;
          }}
        />
      )}
      {!previewGifUrl && !isGeneratingGif && fallbackImageSrc && (
        <img
          src={fallbackImageSrc}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
          alt="Thumbnail"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultImage;
          }}
        />
      )}
    </div>
  );

  const options =
    product?.map((item) => ({
      value: item._id,
      label: item.productName,
    })) || [];

  const validateForm = () => {
    const errors = {};

    if (!sellerType) {
      errors.sellerType = "Seller is Required !";
    }

    // file or link
    if (!video.file && !videoPath) {
      errors.video = "Please select a video!";
    }

    if (
      (Number(videoType) === VIDEO_TYPE.FILE && selectedVideo) ||
      (Number(videoType) === VIDEO_TYPE.LINK &&
        videoPath &&
        gifGenerationRequestedRef.current)
    ) {
      if (isGeneratingGif) {
        errors.previewGif = "GIF preview is still generating. Please wait.";
      } else if (!previewGif) {
        errors.previewGif =
          previewGifError || "GIF preview could not be generated.";
      }
    } else if (!thumbnail) {
      errors.thumbnail = "Please select a Thumbnail !";
    }

    return errors;
  };

  const handleSubmit = async () => {
    if (!hasPermission) {
      permissionError();
      return;
    }
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setError({ ...INITIAL_ERROR_STATE, ...validationErrors });
      return;
    }


    const formData = new FormData();

    formData.append("sellerId", sellerType);
    formData.append("productIds", selectedOptions.join(","));
    formData.append("videoType", videoType);
    formData.append("duration", duration);

    // video
    if (mongoId && !video.file) {
      // edit mode & user ne naya file nahi diya
      formData.append("video", videoPath);
    } else {
      if (Number(videoType) === VIDEO_TYPE.LINK) {
        formData.append("video", videoPath);
      } else {
        formData.append("video", video?.file);
      }
    }

    if (previewGif) {
      formData.append("previewGif", previewGif);
    }

    if (thumbnail) {
      formData.append("thumbnail", thumbnail);
    }

    try {
      if (mongoId) {
        await props.updateFakeReel(formData, sellerType, mongoId);
      } else {
        await props.createFakeReel(formData);
      }
      dispatch({ type: CLOSE_DIALOGUE });
    } catch (err) {
      console.error("Error submitting form:", err);
    }
  };

  return (
    <>
      <div className="mainDialogue fade-in">
        <div className="Dialogue">
          <div className="dialogueHeader">
            <div className="headerTitle fw-bold">Fake Reel</div>
            <div
              className="closeBtn "
              onClick={() => {
                dispatch({ type: CLOSE_DIALOGUE });
              }}
            >
              <i className="fa-solid fa-xmark"></i>
            </div>
          </div>

          <div className="dialogueMain">
            <div className="row">
              {/* Seller */}
              <div className="col-12">
                <label
                  className="styleForTitle mb-2"
                  style={{ color: "#000", fontSize: "14px" }}
                >
                  Seller
                </label>
                <select
                  name="type"
                  className="form-control form-control-line"
                  id="type"
                  value={sellerType}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSellerType(value);
                    if (!value) {
                      setError((prev) => ({
                        ...prev,
                        sellerType: "Seller is Required !",
                      }));
                    } else {
                      setError((prev) => ({ ...prev, sellerType: "" }));
                      setSelectedOptions([]);
                      props.getSellerProduct(value);
                    }
                  }}
                >
                  <option value="" disabled>
                    Select seller
                  </option>
                  {fakeSeller?.map((data) => (
                    <option key={data?._id} value={data?._id}>
                      {data.firstName + " " + data.lastName}
                    </option>
                  ))}
                </select>
                {error.sellerType && (
                  <div className="pl-1 text-left">
                    <p className="errorMessage">{error.sellerType}</p>
                  </div>
                )}
              </div>

              {/* Products multi-select */}
              <div className="col-12">
                <label
                  className="styleForTitle my-2"
                  style={{
                    color: "#000",
                    fontSize: "14px",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  Product
                </label>
                <Select
                  style={{ width: "100%" }}
                  multiple
                  value={selectedOptions}
                  onChange={(e) => setSelectedOptions(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <span style={{ color: "#000", fontSize: "15px" }}>
                          Select products
                        </span>
                      );
                    }
                    return selected
                      .map(
                        (value) =>
                          options.find((option) => option.value === value)
                            ?.label
                      )
                      .join(", ");
                  }}
                  sx={{
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#b93160",
                    },
                  }}
                >
                  {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {/* Duration display (read only) */}
              <div className="col-12 mt-2">
                <Input
                  label="Duration (seconds)"
                  id="duration"
                  type="text"
                  value={duration ? duration.toFixed(2) : 0}
                  disabled
                  readOnly
                />
              </div>

              {/* Video type */}
              <div className="col-12">
                <div className="d-flex align-items-center mt-3">
                  <label
                    className="mb-3"
                    style={{ color: "#000", fontSize: "14px" }}
                  >
                    Video Type :-
                  </label>
                  <Input
                    label="File"
                    name="file"
                    id="file"
                    type="radio"
                    value={VIDEO_TYPE.FILE}
                    checked={Number(videoType) === VIDEO_TYPE.FILE}
                    newClass="me-3 ms-2 mb-2"
                    onClick={(e) => setVideoType(parseInt(e.target.value, 10))}
                  />
                  <Input
                    label="Link"
                    name="file"
                    id="link"
                    type="radio"
                    value={VIDEO_TYPE.LINK}
                    newClass="mb-2"
                    checked={Number(videoType) === VIDEO_TYPE.LINK}
                    onClick={(e) => setVideoType(parseInt(e.target.value, 10))}
                  />
                </div>
              </div>

              {/* File upload */}
              <div className={Number(videoType) === VIDEO_TYPE.FILE ? "col-12" : "d-none"}>
                <Input
                  label="Video"
                  id="video"
                  type="file"
                  accept="video/*"
                  errorMessage={error.video}
                  onChange={handleVideo}
                />

                {selectedVideo ? (
                  <div style={{ marginTop: "8px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <video
                        controls
                        style={{ width: "200px", height: "200px" }}
                        src={videoPath}
                        crossOrigin="anonymous"
                      />
                      {renderGifPreviewBox()}
                    </div>
                    {(previewGifError || error.previewGif) && (
                      <div className="pl-1 text-left">
                        <p className="errorMessage">
                          {previewGifError || error.previewGif}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {videoPath && thumbnail && (
                      <div className="">
                        <video
                          controls
                          style={{ width: "200px", height: "200px" }}
                          src={videoPath}
                          crossOrigin="anonymous"
                        />
                        <img
                          src={thumbnail}
                          style={{
                            width: "200px",
                            height: "200px",
                            position: "absolute",
                            marginLeft: "36px",
                          }}
                          alt="Thumbnail"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = defaultImage;
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Link upload */}
              <div className={Number(videoType) === VIDEO_TYPE.LINK ? "col-12" : "d-none"}>
                <Input
                  label="Link"
                  placeholder="link"
                  id="link"
                  type="text"
                  value={videoPath || ""}
                  errorMessage={error.video}
                  onChange={(e) => handleVideoLinkChange(e.target.value)}
                />
                {videoPath && (
                  <div style={{ marginTop: "8px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <video
                        controls
                        style={{ width: "200px", height: "200px" }}
                        src={videoPath}
                        draggable="false"
                        className="m-0"
                        onError={(e) => {
                          console.error("Video error code:", e.target.error?.code);
                          console.error(
                            "Video error message:",
                            e.target.error?.message
                          );
                        }}
                      >
                        <source src={videoPath} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                      {renderGifPreviewBox(thumbnailPath || thumbnail)}
                    </div>
                    {(previewGifError || error.previewGif) && (
                      <div className="pl-1 text-left">
                        <p className="errorMessage">
                          {previewGifError || error.previewGif}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="dialogueFooter">
            <div className="dialogueBtn">
              <Button
                btnName={mongoId ? "Update" : "Submit"}
                btnColor="btnBlackPrime text-light"
                style={{ borderRadius: "5px", width: "80px" }}
                newClass="me-2"
                onClick={handleSubmit}
              />
              <Button
                btnName="Close"
                btnColor="myCustomButton"
                style={{ borderRadius: "5px", width: "80px" }}
                onClick={() => {
                  dispatch({ type: CLOSE_DIALOGUE });
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(null, {
  getFakeSellerDropDown,
  createFakeReel,
  updateFakeReel,
  getSellerProduct,
})(FakeReelDialogue);
